import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { Button, useMedia, theme } from '@landsbankinn/ui';
import getYear from 'date-fns/getYear';
import React from 'react';
import type { fetchFunds_funds_nodes } from '../../../../graphql/__generated__/fetchFunds';
import {
  MarketsBodyBold,
  MarketsNumberSmall,
  MarketsSmall,
} from '../../shared/MarketsComponents';
import {
  MarketsFormatNumber,
  NumberType,
} from '../../shared/MarketsFormatNumber';
import { NominalReturnOptions } from '../returns-selector-tabs/useReturnsConfig';
import { RiskRating, RiskRatingSize } from '../risk-rating/RiskRating';
import res from './../Funds.res';

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 80px;
`;

export function useNominalData(
  data: fetchFunds_funds_nodes[],
  category: string,
  selectedReturn: NominalReturnOptions,
) {
  const { r } = useResource();
  const isMobile = useMedia('sm');

  const currentYear = getYear(new Date());
  return React.useMemo(() => {
    if (isMobile) {
      const mobileArray = [
        {
          Header: category,
          accessor: 'shortName',
          headerStyle: {
            flexDirection: 'row-reverse',
          },
          Cell: ({ value, row: { original: fund } }: any) => (
            <MarketsBodyBold>
              {value}
              {fund.details?.ucitsClassification
                .toLocaleLowerCase()
                .includes('non-ucits') && <>*</>}
            </MarketsBodyBold>
          ),
        },
        {
          accessor: 'currency',
          style: {
            textAlign: 'right',
          },
          Cell: ({ value }: any) => <MarketsSmall>{value}</MarketsSmall>,
        },
        {
          accessor: 'details.riskRating',
          Cell: ({ value }: any) => (
            <RiskRating
              value={value}
              size={isMobile ? RiskRatingSize.Small : RiskRatingSize.Normal}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        selectedReturn === NominalReturnOptions.OneMonthReturn
          ? {
              accessor: 'cumulativeReturns.oneMonthReturn',
              sortType: 'basic',
              Cell: ({ value }: any) => (
                <MarketsNumberSmall
                  type={NumberType.Percentage}
                  decimalScale={2}
                  hideZero
                  hideNullIndicator
                  displayPosNegColor
                  number={value}
                />
              ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === NominalReturnOptions.ThreeMonthReturn
          ? {
              accessor: 'cumulativeReturns.threeMonthReturn',
              sortType: 'basic',
              Cell: ({ value }: any) => (
                <MarketsNumberSmall
                  type={NumberType.Percentage}
                  decimalScale={2}
                  hideZero
                  hideNullIndicator
                  displayPosNegColor
                  number={value}
                />
              ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === NominalReturnOptions.SixMonthReturn
          ? {
              accessor: 'cumulativeReturns.sixMonthReturn',
              sortType: 'basic',
              Cell: ({ value }: any) => (
                <MarketsNumberSmall
                  type={NumberType.Percentage}
                  decimalScale={2}
                  hideZero
                  hideNullIndicator
                  displayPosNegColor
                  number={value}
                />
              ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === NominalReturnOptions.YearToDateReturn
          ? {
              accessor: 'cumulativeReturns.yearToDateReturn',
              sortType: 'basic',
              Cell: ({ value }: any) => (
                <MarketsNumberSmall
                  type={NumberType.Percentage}
                  decimalScale={2}
                  hideZero
                  hideNullIndicator
                  displayPosNegColor
                  number={value}
                />
              ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === NominalReturnOptions.OneYearPast
          ? {
              id: 'oneYearPast',
              sortType: 'basic',
              accessor: (fund: any) =>
                fund.nominalReturns.find(
                  (nominalReturn: any) =>
                    nominalReturn.year === currentYear - 1,
                )?.return ?? '',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === NominalReturnOptions.TwoYearPast
          ? {
              id: 'twoYearPast',
              sortType: 'basic',
              accessor: (fund: any) =>
                fund.nominalReturns.find(
                  (nominalReturn: any) =>
                    nominalReturn.year === currentYear - 2,
                )?.return ?? '',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === NominalReturnOptions.ThreeYearPast
          ? {
              id: 'threeYearPast',
              sortType: 'basic',
              accessor: (fund: any) =>
                fund.nominalReturns.find(
                  (nominalReturn: any) =>
                    nominalReturn.year === currentYear - 3,
                )?.return ?? '',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === NominalReturnOptions.FourYearPast
          ? {
              id: 'fourYearPast',
              sortType: 'basic',
              accessor: (fund: any) =>
                fund.nominalReturns.find(
                  (nominalReturn: any) =>
                    nominalReturn.year === currentYear - 4,
                )?.return ?? '',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === NominalReturnOptions.FiveYearPast
          ? {
              id: 'fiveYearPast',
              sortType: 'basic',
              accessor: (fund: any) =>
                fund.nominalReturns.find(
                  (nominalReturn: any) =>
                    nominalReturn.year === currentYear - 5,
                )?.return ?? '',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
      ];
      return mobileArray.filter(function (i) {
        return i != null;
      });
    } else {
      return [
        {
          Header: category,
          accessor: 'shortName',
          headerStyle: {
            flexDirection: 'row-reverse',
          },
          Cell: ({ value, row: { original: fund } }: any) => (
            <MarketsBodyBold>
              {value}
              {fund.details?.ucitsClassification
                .toLocaleLowerCase()
                .includes('non-ucits') && <>*</>}
            </MarketsBodyBold>
          ),
        },
        {
          Header: r(res.currency),
          accessor: 'currency',
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.oneMonth),
          accessor: 'cumulativeReturns.oneMonthReturn',
          sortType: 'basic',
          Cell: ({ value }: any) => (
            <MarketsFormatNumber
              type={NumberType.Percentage}
              decimalScale={2}
              hideZero
              hideNullIndicator
              displayPosNegColor
              number={value}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.threeMonths),
          accessor: 'cumulativeReturns.threeMonthReturn',
          sortType: 'basic',
          Cell: ({ value }: any) => (
            <MarketsFormatNumber
              type={NumberType.Percentage}
              decimalScale={2}
              hideZero
              hideNullIndicator
              displayPosNegColor
              number={value}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.sixMonths),
          accessor: 'cumulativeReturns.sixMonthReturn',
          sortType: 'basic',
          Cell: ({ value }: any) => (
            <MarketsFormatNumber
              type={NumberType.Percentage}
              decimalScale={2}
              hideZero
              hideNullIndicator
              displayPosNegColor
              number={value}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.yearToDate),
          accessor: 'cumulativeReturns.yearToDateReturn',
          sortType: 'basic',
          Cell: ({ value }: any) => (
            <MarketsFormatNumber
              type={NumberType.Percentage}
              decimalScale={2}
              hideZero
              hideNullIndicator
              displayPosNegColor
              number={value}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          id: 'oneYearPast',
          sortType: 'basic',
          Header: currentYear - 1,
          accessor: (fund: any) =>
            fund.nominalReturns.find(
              (nominalReturn: any) => nominalReturn.year === currentYear - 1,
            )?.return ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },
        {
          id: 'twoYearPast',
          sortType: 'basic',
          Header: currentYear - 2,
          accessor: (fund: any) =>
            fund.nominalReturns.find(
              (nominalReturn: any) => nominalReturn.year === currentYear - 2,
            )?.return ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },
        {
          id: 'threeYearPast',
          sortType: 'basic',
          Header: currentYear - 3,
          accessor: (fund: any) =>
            fund.nominalReturns.find(
              (nominalReturn: any) => nominalReturn.year === currentYear - 3,
            )?.return ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },
        {
          id: 'fourYearPast',
          sortType: 'basic',
          Header: currentYear - 4,
          accessor: (fund: any) =>
            fund.nominalReturns.find(
              (nominalReturn: any) => nominalReturn.year === currentYear - 4,
            )?.return ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },
        {
          id: 'fiveYearPast',
          sortType: 'basic',
          Header: currentYear - 5,
          accessor: (fund: any) =>
            fund.nominalReturns.find(
              (nominalReturn: any) => nominalReturn.year === currentYear - 5,
            )?.return ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.volatility),
          accessor: 'details.riskRating',
          Cell: ({ value }: any) => <RiskRating value={value} />,
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: '',
          accessor: 'details.onlineBuyAllowed',
          Cell: ({ value, row: { original: fund } }: any) =>
            value ? (
              <ButtonContainer>
                <Button
                  as="a"
                  title={r(res.buy)}
                  isSmall
                  href={
                    fund.symbol !== 'LEQ'
                      ? 'https://netbanki.landsbankinn.is/Securities/Overview/Overview.aspx#/funds/market'
                      : 'https://netbanki.landsbankinn.is/Securities/Overview/Overview.aspx#/stocks/market'
                  }
                  bg={theme.colors.primary}
                  onClick={(e) => e.stopPropagation()}
                />
              </ButtonContainer>
            ) : (
              <ButtonContainer></ButtonContainer>
            ),
          style: {
            textAlign: 'right',
          },
        },
      ];
    }
  }, [data, selectedReturn]);
}
